import {
  BarChart3,
  BookOpen,
  Building2,
  ClipboardCheck,
  FileText,
  FlaskConical,
  Gauge,
  Gavel,
  GitBranch,
  Lightbulb,
  LineChart,
  ListChecks,
  Inbox,
  MessagesSquare,
  PenLine,
  ScrollText,
  Settings,
  Search,
  Stamp,
  Tags,
  TerminalSquare,
} from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { apiGet } from '../../api/client';
import { useTikTokWaitingCount } from '../../api/tickets';
import { describeAccess, initialsFor, isSuperadmin, useSession } from '../../auth/session';
import { cn } from '../../lib/cn';
import { intentLabel } from '../../lib/inbox/rows';

interface SearchHit {
  id: string;
  externalId: string;
  customerName: string | null;
  customerEmail: string | null;
  intent: string | null;
  status: string;
  brand: { name: string };
}

/**
 * The header search, finally wired: a Zendesk ticket id (with or without the
 * leading '#'), a customer email, or a name — pasted from Zendesk, landing on
 * the same ticket here. The input rendered for weeks doing nothing, which is
 * worse than no input: it taught people the search does not work.
 */
function GlobalSearch() {
  const navigate = useNavigate();
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(term.trim()), 250);
    return () => clearTimeout(timer);
  }, [term]);

  // Click-away closes the dropdown; the query cache keeps the results.
  useEffect(() => {
    const onPointerDown = (event: PointerEvent) => {
      if (boxRef.current && !boxRef.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, []);

  const results = useQuery({
    queryKey: ['ticket-search', debounced],
    queryFn: () => apiGet<SearchHit[]>(`/api/tickets/search?q=${encodeURIComponent(debounced)}`),
    enabled: debounced.length >= 2,
  });

  const hits = debounced.length >= 2 ? (results.data ?? []) : [];

  const openTicket = (id: string) => {
    setOpen(false);
    setTerm('');
    navigate(`/tickets/${id}`);
  };

  return (
    <div ref={boxRef} className="relative ml-auto w-72">
      <label className="flex items-center gap-2 rounded bg-surface-sunken px-2.5 py-1.5 shadow-hairline focus-within:shadow-none focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-accent-500">
        <Search size={14} strokeWidth={1.75} className="text-ink-400" aria-hidden />
        <span className="sr-only">Search tickets</span>
        <input
          type="search"
          value={term}
          onChange={(event) => {
            setTerm(event.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(event) => {
            if (event.key === 'Escape') setOpen(false);
            if (event.key === 'Enter' && hits[0]) openTicket(hits[0].id);
          }}
          placeholder="Search tickets: Zendesk id, email, name"
          className="w-full bg-transparent text-xs text-ink-900 placeholder:text-ink-400 focus:outline-none"
        />
      </label>

      {open && debounced.length >= 2 ? (
        <div className="absolute right-0 top-full z-50 mt-1 max-h-96 w-96 overflow-y-auto rounded border border-line bg-surface shadow-overlay">
          {results.isLoading ? (
            <p className="px-3 py-3 text-xs text-ink-400">Searching…</p>
          ) : hits.length === 0 ? (
            <p className="px-3 py-3 text-xs text-ink-400">
              No ticket matches. Playground sessions are not searched — find those in the
              Playground log.
            </p>
          ) : (
            <ul>
              {hits.map((hit) => (
                <li key={hit.id}>
                  <button
                    type="button"
                    onClick={() => openTicket(hit.id)}
                    className="block w-full border-b border-line px-3 py-2 text-left transition-colors duration-fast hover:bg-surface-sunken"
                  >
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="truncate text-xs font-semibold text-ink-900">
                        {hit.customerName ?? hit.customerEmail ?? 'Unknown sender'}
                      </span>
                      <span className="shrink-0 font-mono text-2xs text-ink-400">
                        #{hit.externalId}
                      </span>
                    </div>
                    <div className="mt-0.5 truncate text-2xs text-ink-400">
                      {hit.brand.name} · {intentLabel(hit.intent)} · {hit.status.replace(/_/g, ' ')}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}

/**
 * The signed-in user, read from the session.
 *
 * This was hardcoded to "Deivydas B. · superadmin · all brands", copied from
 * the mockup and never replaced. Alongside the missing route guard it meant the
 * console looked like an authenticated superadmin session to anyone who opened
 * the URL. Never render an identity the server did not assert.
 */
function SignedInAs() {
  const { data } = useSession();
  if (!data) return null;

  return (
    <div className="mt-auto border-t border-line px-3 pt-3">
      <div className="flex items-center gap-2.5">
        <span
          aria-hidden
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-accent-100 text-2xs font-semibold text-accent-700"
        >
          {initialsFor(data)}
        </span>
        <div className="min-w-0">
          <div className="truncate text-xs font-medium text-ink-900" title={data.email}>
            {data.name ?? data.email}
          </div>
          <div className="truncate text-2xs text-ink-400">{describeAccess(data)}</div>
        </div>
      </div>
      <a
        href="/auth/logout"
        className="mt-2 inline-block text-2xs text-ink-400 transition-colors duration-fast hover:text-ink-900"
      >
        Sign out
      </a>
    </div>
  );
}

/**
 * The shell.
 *
 * A dense, quiet chrome: 224px rail, 48px bar, hairline separators, no
 * decoration. Everything colourful on screen should be a status, so the frame
 * itself is deliberately monochrome.
 */

interface NavItem {
  to: string;
  label: string;
  icon: typeof Inbox;
  count?: number;
}

/**
 * The nav carried two invented counts — 28 in the inbox, 6 QA reviews — and a
 * link to a ticket id from the mockup that does not exist. All three came from
 * the fixtures era. Only the approval count is real, because only it is
 * counted; the rest carry no badge rather than a decorative one.
 */
// Insights leads and the Dashboard leads Insights — it is also where "/"
// lands after sign-in. The day starts with how it is going, then the work
// queue (rail order ruled by Deivydas 2026-09-18).
const INSIGHTS: NavItem[] = [
  // Reports merged into the Dashboard's sub-tabs (Deivydas 2026-09-18).
  { to: '/dashboard', label: 'Dashboard', icon: BarChart3 },
];

// Superadmin only, matching the API's own gate: model spend and failure
// details are tech territory, not the ops team's day.
const ENGINE: NavItem = { to: '/engine', label: 'Engine', icon: Gauge };

// Everyone: the config-explainer chat is FOR the ops team asking "how is
// the agent set up to…" without routing the question through Deivydas.
const ASK: NavItem = { to: '/ask', label: 'Ask Zoye', icon: MessagesSquare };

// Audit log and QA reviews moved here from Insights (Deivydas 2026-09-18):
// they are work surfaces, not charts.
const OPERATE: NavItem[] = [
  { to: '/inbox', label: 'Inbox', icon: Inbox },
  { to: '/approvals', label: 'Approvals', icon: Stamp },
  { to: '/audit', label: 'Audit log', icon: ScrollText },
  { to: '/qa', label: 'QA reviews', icon: ClipboardCheck },
];

const BUILD: NavItem[] = [
  { to: '/workflows', label: 'Workflows', icon: GitBranch },
  { to: '/rules', label: 'Bot rules', icon: Gavel },
  { to: '/knowledge', label: 'Knowledge', icon: BookOpen },
  { to: '/prompts', label: 'Prompts', icon: FileText },
  { to: '/taxonomies', label: 'Taxonomies', icon: Tags },
  { to: '/playground', label: 'Playground', icon: TerminalSquare },
  { to: '/playground-log', label: 'Playground log', icon: ScrollText },
];

const IMPROVE: NavItem[] = [
  { to: '/corrections', label: 'Corrections', icon: PenLine },
  { to: '/proposals', label: 'Proposals', icon: Lightbulb },
  { to: '/evals', label: 'Import cases', icon: FlaskConical },
  { to: '/evals/review', label: 'Review cases', icon: ListChecks },
  { to: '/evals/runs', label: 'Eval runs', icon: LineChart },
];

const SETTINGS: NavItem[] = [{ to: '/admin', label: 'Admin', icon: Settings }];

// The "coming in W4" ghost rows are gone: every screen in the rail is real.

function NavGroup({
  label,
  items,
  trailing,
}: {
  label?: string;
  items: NavItem[];
  trailing?: ReactNode;
}) {
  return (
    <div className="mb-4">
      {label ? <div className="eyebrow px-3 pb-1.5">{label}</div> : null}
      <ul className="space-y-px">
        {items.map((item) => (
          <li key={item.to}>
            <NavLink
              to={item.to}
              className={({ isActive }) =>
                cn(
                  'group flex items-center gap-2.5 rounded px-3 py-1.5 text-sm transition-colors duration-fast',
                  isActive
                    ? 'bg-accent-50 font-semibold text-accent-700'
                    : 'text-ink-500 hover:bg-surface-sunken hover:text-ink-900',
                )
              }
            >
              <item.icon size={15} strokeWidth={1.75} aria-hidden />
              <span className="truncate">{item.label}</span>
              {item.count !== undefined ? (
                <span className="tabular ml-auto rounded-sm bg-surface-sunken px-1.5 py-px text-2xs font-semibold text-ink-500 shadow-hairline">
                  {item.count}
                </span>
              ) : null}
            </NavLink>
          </li>
        ))}
        {trailing}
      </ul>
    </div>
  );
}

const TITLES: Record<string, string> = {
  '/inbox': 'Inbox',
  '/approvals': 'Approvals',
  '/dashboard': 'Dashboard',
  '/engine': 'Engine',
  '/ask': 'Ask Zoye',
  '/audit': 'Audit log',
  '/workflows': 'Workflows',
  '/rules': 'Bot rules',
  '/taxonomies': 'Taxonomies',
  '/playground': 'Playground',
  '/knowledge': 'Knowledge base',
  '/qa': 'QA reviews',
  '/corrections': 'Corrections',
  '/proposals': 'Proposals',
  '/prompts': 'Prompts',
  '/evals': 'Import eval cases',
  '/evals/review': 'Review eval cases',
  '/evals/runs': 'Eval runs',
  '/admin': 'Admin',
};

export function AppShell({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const session = useSession();
  const title =
    TITLES[pathname] ?? (pathname.startsWith('/tickets') ? 'Ticket workspace' : 'Zoye');

  // The one badge that is a real count. It fails quietly: a nav that 403s for
  // a viewer must not take the whole shell down with it.
  const { data: analytics } = useQuery({
    queryKey: ['analytics'],
    queryFn: () => apiGet<{ actions: { awaitingApproval: number } }>('/api/analytics/summary'),
    retry: false,
  });
  // TikTok chat replies waiting for a Send count too (Deivydas 2026-10-26).
  const tiktokWaiting = useTikTokWaitingCount();
  const awaiting = (analytics?.actions.awaitingApproval ?? 0) + (tiktokWaiting.data ?? 0);

  const operate = OPERATE.map((item) =>
    item.to === '/approvals' && awaiting > 0 ? { ...item, count: awaiting } : item,
  );

  // The Engine row appears only for a superadmin — the API refuses everyone
  // else anyway, and a menu item that always 403s just teaches distrust.
  const insights =
    session.data && isSuperadmin(session.data) ? [...INSIGHTS, ENGINE, ASK] : [...INSIGHTS, ASK];

  return (
    <div className="flex h-screen overflow-hidden bg-paper">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-3 focus:top-3 focus:z-50 focus:rounded focus:bg-surface focus:px-3 focus:py-2 focus:shadow-overlay"
      >
        Skip to content
      </a>

      <nav
        aria-label="Primary"
        className="flex w-56 shrink-0 flex-col overflow-y-auto border-r border-line bg-surface px-2.5 py-3"
      >
        <div className="mb-5 flex items-center gap-2.5 px-3 pt-1">
          {/* A flat monogram tile. One accent colour, no gradient. */}
          <span
            aria-hidden
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-accent-600 text-xs font-semibold text-accent-contrast"
          >
            IB
          </span>
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold tracking-tight text-ink-900">Zoye</div>
            <div className="truncate text-2xs text-ink-400">AI CS Agents</div>
          </div>
        </div>

        <NavGroup label="Insights" items={insights} />
        <NavGroup label="Operate" items={operate} />
        <NavGroup label="Build" items={BUILD} />
        <NavGroup label="Improve" items={IMPROVE} />
        <NavGroup label="Settings" items={SETTINGS} />

        <SignedInAs />
      </nav>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-14 shrink-0 items-center gap-4 border-b border-line bg-surface px-5">
          <h1 className="text-lg font-semibold tracking-tight text-ink-900">{title}</h1>
          {/* The caller's own access, not a hardcoded "All brands (14)" —
              which claimed superadmin reach for everybody who opened the page. */}
          {session.data ? (
            <span className="flex items-center gap-1.5 rounded-sm px-1.5 py-0.5 text-2xs text-ink-500 shadow-hairline">
              <Building2 size={12} strokeWidth={1.75} aria-hidden />
              {describeAccess(session.data)}
            </span>
          ) : null}
          <GlobalSearch />
        </header>

        <main id="main" className="min-h-0 flex-1 overflow-auto">
          {children}
        </main>
      </div>
    </div>
  );
}
